import React, { useEffect, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import CircularProgress from "@material-ui/core/CircularProgress";
import PermIdentity from "@material-ui/icons/PermIdentity";

import GridContainer from "components/Grid/GridContainer.js";
import GridItem from "components/Grid/GridItem.js";
import Button from "components/CustomButtons/Button.js";
import CustomInput from "components/CustomInput/CustomInput.js";
import Clearfix from "components/Clearfix/Clearfix.js";
import Card from "components/Card/Card.js";
import CardBody from "components/Card/CardBody.js";
import CardHeader from "components/Card/CardHeader.js";
import CardIcon from "components/Card/CardIcon.js";
import Snackbar from "components/Snackbar/Snackbar.js";
import CheckCircle from "@material-ui/icons/CheckCircle";
import ErrorIcon from "@material-ui/icons/Error";

import styles from "assets/jss/material-dashboard-pro-react/views/userProfileStyles.js";

const useStyles = makeStyles(styles);

const GRAPHQL_URL = (process.env.REACT_APP_API_URL || "http://localhost:5001") + "/graphql";

const GET_USER_INFO = `
  query GetUserInfo {
    getUserInfo {
      id
      name
      firstName
      lastName
      email
      phoneNumber
      isActive
      departments { code name }
    }
  }
`;

const UPDATE_USER = `
  mutation UpdateUser($data: UpdateUserDto!) {
    updateUser(data: $data) {
      id
      name
      firstName
      lastName
      phoneNumber
      email
    }
  }
`;

function gql(query, variables) {
  const token = localStorage.getItem("token");
  return fetch(GRAPHQL_URL, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: token ? `Bearer ${token}` : "",
    },
    credentials: "include",
    body: JSON.stringify({ query, variables }),
  }).then((r) => r.json());
}

export default function UserProfile() {
  const classes = useStyles();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [userMeta, setUserMeta] = useState(null); // id, email, isActive, departments
  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    phoneNumber: "",
  });
  const [notification, setNotification] = useState({ open: false, message: "", color: "success" });

  useEffect(() => {
    gql(GET_USER_INFO)
      .then(({ data, errors }) => {
        if (errors || !data?.getUserInfo) return;
        const u = data.getUserInfo;
        setUserMeta({
          id: u.id,
          email: u.email,
          isActive: u.isActive,
          departments: (u.departments || []).map((d) => d.code),
        });
        setForm({
          firstName: u.firstName || "",
          lastName: u.lastName || "",
          phoneNumber: u.phoneNumber || "",
        });
      })
      .finally(() => setLoading(false));
  }, []);

  const handleChange = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  const handleSave = async () => {
    if (!userMeta) return;
    setSaving(true);
    const { data, errors } = await gql(UPDATE_USER, {
      data: {
        id: userMeta.id,
        email: userMeta.email,
        isActive: userMeta.isActive,
        departments: userMeta.departments,
        firstName: form.firstName,
        lastName: form.lastName,
        phoneNumber: form.phoneNumber,
      },
    });
    setSaving(false);
    if (errors || !data?.updateUser) {
      setNotification({ open: true, message: errors?.[0]?.message || "Update failed", color: "danger" });
    } else {
      setNotification({ open: true, message: "Profile updated successfully", color: "success" });
    }
  };

  if (loading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", marginTop: 80 }}>
        <CircularProgress color="secondary" />
      </div>
    );
  }

  return (
    <div>
      <Snackbar
        place="tr"
        color={notification.color}
        icon={notification.color === "success" ? CheckCircle : ErrorIcon}
        message={notification.message}
        open={notification.open}
        closeNotification={() => setNotification((n) => ({ ...n, open: false }))}
        close
      />
      <GridContainer justifyContent="center">
        <GridItem xs={12} sm={12} md={8}>
          <Card>
            <CardHeader color="rose" icon>
              <CardIcon color="rose">
                <PermIdentity />
              </CardIcon>
              <h4 className={classes.cardIconTitle}>
                Edit Profile — <small>Update your personal information</small>
              </h4>
            </CardHeader>
            <CardBody>
              <GridContainer>
                <GridItem xs={12} sm={12} md={6}>
                  <CustomInput
                    labelText="First Name"
                    id="first-name"
                    formControlProps={{ fullWidth: true }}
                    inputProps={{
                      value: form.firstName,
                      onChange: handleChange("firstName"),
                    }}
                  />
                </GridItem>
                <GridItem xs={12} sm={12} md={6}>
                  <CustomInput
                    labelText="Last Name"
                    id="last-name"
                    formControlProps={{ fullWidth: true }}
                    inputProps={{
                      value: form.lastName,
                      onChange: handleChange("lastName"),
                    }}
                  />
                </GridItem>
              </GridContainer>
              <GridContainer>
                <GridItem xs={12} sm={12} md={6}>
                  <CustomInput
                    labelText="Email (read only)"
                    id="email"
                    formControlProps={{ fullWidth: true }}
                    inputProps={{
                      value: userMeta?.email || "",
                      disabled: true,
                    }}
                  />
                </GridItem>
                <GridItem xs={12} sm={12} md={6}>
                  <CustomInput
                    labelText="Phone Number"
                    id="phone-number"
                    formControlProps={{ fullWidth: true }}
                    inputProps={{
                      value: form.phoneNumber,
                      onChange: handleChange("phoneNumber"),
                    }}
                  />
                </GridItem>
              </GridContainer>
              <Button color="rose" className={classes.updateProfileButton} onClick={handleSave} disabled={saving}>
                {saving ? "Saving..." : "Update Profile"}
              </Button>
              <Clearfix />
            </CardBody>
          </Card>
        </GridItem>
      </GridContainer>
    </div>
  );
}
